import React, { useState } from 'react';
import { Item, Board } from '@/stores/canvasStore';
import { TweetPreviewCard, isTweetItem } from '@/components/items/TweetPreviewCard';
import {
  FileText,
  File,
  StickyNote,
  Type,
  Image as ImageIcon,
  Link2,
  Film,
  Play,
  Clock,
  Pin,
  Share2,
  Trash2,
  Eye,
  Download,
  ExternalLink,
  Maximize2,
  X
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { copyShareLink } from '@/lib/sharing';
import { supabase } from '@/integrations/supabase/client';
import { getContrastColor, getStickyColorValue } from '@/lib/colorUtils';
import { toast } from 'sonner';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface WallItemCardProps {
  item: Item;
  board?: Board;
  boards?: Board[];
  onClick: () => void;
  onPin?: () => void;
  onDelete?: () => void;
  isPinned?: boolean;
  compact?: boolean;
}

const stickyColors: Record<string, string> = {
  yellow: 'hsl(45 93% 70%)',
  blue: 'hsl(210 85% 80%)',
  pink: 'hsl(340 85% 82%)',
  green: 'hsl(142 65% 72%)',
  gray: 'hsl(220 14% 82%)',
};

const typeLabels: Record<string, string> = {
  image: 'Image',
  sticky: 'Note',
  text_card: 'Text',
  document: 'Doc',
  file: 'File',
  link: 'Link',
  video: 'Video',
};

// Helper to extract first URL from text
const extractFirstUrl = (text: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  const match = text.match(urlRegex);
  return match ? match[0] : null;
};

const TypeIcon = ({ type, className }: { type: string; className?: string }) => {
  const iconProps = { className: cn('w-3 h-3', className) };
  switch (type) {
    case 'image': return <ImageIcon {...iconProps} />;
    case 'sticky': return <StickyNote {...iconProps} />;
    case 'text_card': return <Type {...iconProps} />;
    case 'document': return <FileText {...iconProps} />;
    case 'file': return <File {...iconProps} />;
    case 'link': return <Link2 {...iconProps} />;
    case 'video': return <Film {...iconProps} />;
    default: return null;
  }
};

// Helper to get board label - shows "Home Board" for root board, "Not in any board" for null
const getBoardLabel = (item: Item, board?: Board, boards?: Board[]): { label: string; color: string | null } => {
  // No board assigned — created in Wall without a home
  if (!item.board_id) {
    return { label: 'Not in any board', color: null };
  }
  
  // Find the board if not passed directly
  const foundBoard = board || boards?.find(b => b.id === item.board_id);
  
  if (!foundBoard) {
    return { label: 'Not in any board', color: null };
  }
  
  // Check if it's the Home board (root board with no parent)
  const isHomeBoard = foundBoard.name === 'Home' || foundBoard.parent_id === null;
  
  return {
    label: isHomeBoard ? 'Home Board' : foundBoard.name,
    color: foundBoard.icon_color || '#6366F1'
  };
};

// Context menu component
interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const ContextMenu = ({ x, y, onClose, onShare, onDelete }: ContextMenuProps) => {
  React.useEffect(() => {
    const handleClickOutside = () => onClose();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  return (
    <div
      className="fixed z-50 bg-white dark:bg-[#2a2a2a] rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 min-w-[160px]"
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onShare();
          onClose();
        }}
        className="w-full px-4 py-2.5 text-left flex items-center gap-3 rounded-lg hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 text-gray-700 dark:text-gray-300 transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>
      <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
          onClose();
        }}
        className="w-full px-4 py-2.5 text-left flex items-center gap-3 rounded-lg hover:bg-red-600 hover:text-white text-red-600 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
        Delete
      </button>
    </div>
  );
};

export function WallItemCard({ item, board, boards, onClick, onPin, onDelete, isPinned = false, compact = false }: WallItemCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const content = item.content as Record<string, unknown>;

  // Determine if this is a card that should use 3:2 aspect ratio
  const isPdfFile = item.item_type === 'file' && (item.content as { mimeType?: string }).mimeType === 'application/pdf';
  const useFixedAspect = ['sticky', 'text_card', 'document', 'video'].includes(item.item_type) || isPdfFile;

  // Get board label using helper
  const { label: boardLabel, color: boardColor } = getBoardLabel(item, board, boards);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleShare = async () => {
    await copyShareLink('item', item.id);
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Delete this item? This cannot be undone.');
    if (!confirmed) return;

    try {
      // Delete from storage if it's a file
      if (content?.url && typeof content.url === 'string' && content.url.includes('supabase')) {
        const parts = content.url.split('/');
        const filename = parts.slice(-2).join('/'); // user_id/filename
        await supabase.storage.from('uploads').remove([filename]);
      }
      if (content?.videoUrl && typeof content.videoUrl === 'string' && content.videoUrl.includes('supabase')) {
        const parts = content.videoUrl.split('/');
        const filename = parts.slice(-2).join('/');
        await supabase.storage.from('uploads').remove([filename]);
      }

      // Delete item
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', item.id);

      if (error) {
        toast.error('Failed to delete');
        return;
      }

      toast.success('Item deleted');
      onDelete?.();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete');
    }
  };

  // Check if item has image-dominant display (for board tag styling)
  const isImageDominant = (
    item.item_type === 'image' || 
    (item.item_type === 'link' && !!(content.image))
  );

  // Board tag component - used at bottom right of all cards, only visible on hover
  const BoardTag = () => (
    <div className={cn(
      'absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-opacity duration-200',
      isHovered ? 'opacity-100' : 'opacity-0',
      isImageDominant
        ? 'bg-black/30 backdrop-blur text-white/70'
        : 'bg-muted text-muted-foreground'
    )}>
      {boardColor ? (
        <>
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: boardColor }}
          />
          <span className="truncate max-w-[100px]">{boardLabel}</span>
        </>
      ) : (
        <span className="truncate max-w-[120px] italic">{boardLabel}</span>
      )}
    </div>
  );

  const renderContent = () => {
    switch (item.item_type) {
      case 'image':
        const imageCaption = content.summary as string || content.alt as string || content.filename as string;
        const isGif = (content.mimeType as string) === 'image/gif' || 
          (content.url as string)?.toLowerCase().endsWith('.gif') || 
          (content.alt as string)?.toLowerCase().endsWith('.gif');
        return (
          <div className="relative h-full">
            {isGif && (
              <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide z-10">
                GIF
              </span>
            )}
            <img
              src={content.url as string}
              alt={content.alt as string || 'Image'}
              className="w-full rounded-xl object-cover"
              loading="lazy"
              style={{ minHeight: '200px' }}
            />
            {/* Caption overlay — only if there's meaningful caption text */}
            {imageCaption && imageCaption !== 'Image' && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-3 pt-8 rounded-b-xl">
                <p className="text-white/90 text-xs leading-relaxed line-clamp-1">
                  {imageCaption}
                </p>
              </div>
            )}
          </div>
        );

      case 'sticky':
        const stickyColor = (content.color as string) || 'yellow';
        const stickyCustomColor = content.customColor as string | undefined;
        const stickyColorCode = content.colorCode as string | undefined;
        const bgColor = getStickyColorValue(stickyColor, stickyCustomColor);
        const textColor = stickyCustomColor ? getContrastColor(stickyCustomColor) : '#1F2937';
        return (
          <div
            className="p-5 h-full flex flex-col relative"
            style={{ backgroundColor: bgColor }}
          >
            <p 
              className="text-sm leading-relaxed whitespace-pre-wrap line-clamp-6 flex-1"
              style={{ color: textColor }}
            >
              {content.text as string || 'Empty note'}
            </p>
            {/* Color code label — bottom right */}
            {stickyColorCode && (
              <div 
                className="absolute bottom-2 right-2.5 text-[9px] font-mono"
                style={{ opacity: 0.3, color: textColor }}
              >
                {stickyColorCode}
              </div>
            )}
            {/* Type indicator at bottom left */}
            <div className="mt-auto pt-2 flex items-center gap-1.5" style={{ color: '#6B7280' }}>
              <StickyNote className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Note</span>
            </div>
          </div>
        );

      case 'text_card':
        const textContent = item.content as { body?: string; title?: string; partnr_generated?: boolean };
        const bodyText = (textContent.body || '').replace(/<[^>]*>/g, '');
        const detectedUrl = extractFirstUrl(bodyText);
        
        // If text card contains a URL, render as link preview
        if (detectedUrl) {
          let linkHostname = '';
          try {
            linkHostname = new URL(detectedUrl).hostname;
          } catch {
            linkHostname = detectedUrl;
          }
          
          return (
            <div className={cn(
              "bg-white dark:bg-[#2a2a2a] overflow-hidden h-full flex flex-col",
              textContent.partnr_generated && "border-2 border-dashed border-partnr/25"
            )}>
              <div className="p-4 flex-1 flex flex-col min-h-[100px]">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">
                  {linkHostname}
                </p>
                {textContent.title && (
                  <h3 className="text-gray-900 dark:text-white font-medium text-sm mb-1 line-clamp-2">
                    {textContent.title}
                  </h3>
                )}
                <p className="text-gray-600 dark:text-gray-400 text-xs line-clamp-3 flex-1">
                  {bodyText.replace(detectedUrl, '').trim() || detectedUrl}
                </p>
                {/* Type indicator */}
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-gray-400">
                  <Link2 className="w-3 h-3" />
                  <span className="text-xs uppercase tracking-wide">Link</span>
                </div>
              </div>
            </div>
          );
        }
        
        const preview = bodyText.slice(0, 250);
        return (
          <div className={cn(
            "p-5 h-full flex flex-col bg-white dark:bg-[#2a2a2a]",
            textContent.partnr_generated && "border-2 border-dashed border-partnr/25"
          )}>
            {textContent.title && (
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {textContent.title}
              </h3>
            )}
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed line-clamp-6 flex-1">
              {preview}
              {bodyText.length > 250 && '...'}
            </p>
            {/* Type indicator */}
            <div className="mt-auto pt-2 flex items-center gap-1.5 text-gray-400">
              <FileText className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Text</span>
            </div>
          </div>
        );

      case 'document':
        // Show more text preview for documents - use 'body' field which contains the HTML
        const docText = (content.body as string || '').replace(/<[^>]*>/g, '');
        const docPreview = docText.slice(0, 300);
        return (
          <div className="bg-white dark:bg-[#2a2a2a] overflow-hidden h-full flex flex-col">
            <div className="px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {content.title as string || 'Untitled Document'}
                </h3>
              </div>
            </div>
            <div className="p-5 flex-1">
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-5">
                {docPreview}
                {docText.length > 300 && '...'}
              </p>
            </div>
            {/* Type indicator */}
            <div className="px-5 pb-4 flex items-center gap-1.5 text-gray-400">
              <File className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Document</span>
            </div>
          </div>
        );

      case 'file': {
        const isPdf = (content.mimeType as string) === 'application/pdf';
        if (isPdf) {
          return (
            <WallPdfPreview
              url={content.url as string}
              filename={content.filename as string || 'Document.pdf'}
              size={content.size as number || 0}
            />
          );
        }
        return (
          <div className="p-4 bg-gray-100 dark:bg-[#2a2a2a] h-full flex flex-col">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700">
                <File className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate text-sm">
                  {content.filename as string}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(content.size as number || 0)}
                </p>
              </div>
            </div>
            {/* Type indicator */}
            <div className="mt-auto pt-2 flex items-center gap-1.5 text-gray-400">
              <File className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">File</span>
            </div>
          </div>
        );
      }

      case 'link': {
        // X/Twitter tweet bookmark — rich preview card
        if (isTweetItem(item)) {
          return <TweetPreviewCard item={item} />;
        }

        const linkUrl = content.url as string;

        // YouTube links get video-card-style embed preview
        const ytId = getYouTubeId(linkUrl);
        if (ytId) {
          const ytTitle = content.title as string || 'YouTube Video';
          const ytThumb = content.image as string || `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
          return (
            <WallYouTubePreview videoId={ytId} title={ytTitle} thumbnail={ytThumb} url={linkUrl} />
          );
        }

        let linkHostname = '';
        try {
          linkHostname = new URL(linkUrl).hostname.replace('www.', '');
        } catch {
          linkHostname = linkUrl;
        }
        const linkTitle = content.title as string || linkHostname;
        // Use screenshot as fallback when no OG image (matches canvas LinkCard behavior)
        const linkImage = (content.image as string) || (content.screenshot as string) || null;

        if (linkImage) {
          // IMAGE-DOMINANT LAYOUT — 85% image, minimal overlay
          return (
            <div className="relative h-full flex flex-col">
              {/* Full card image */}
              <img
                src={linkImage}
                alt={linkTitle}
                className="w-full rounded-xl object-cover"
                loading="lazy"
                style={{ minHeight: '200px' }}
              />
              
              {/* Bottom overlay — title + domain on dark gradient */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent p-4 pt-12 rounded-b-xl">
                <h3 className="text-white font-medium text-sm leading-snug line-clamp-2 mb-1">
                  {linkTitle}
                </h3>
                <div className="flex items-center gap-1.5">
                  {content.favicon && (
                    <img
                      src={content.favicon as string}
                      alt=""
                      className="w-3 h-3 rounded-sm"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span className="text-white/60 text-xs truncate">{linkHostname}</span>
                </div>
              </div>
            </div>
          );
        } else {
          // NO IMAGE FALLBACK — clean text card style
          return (
            <div className="bg-card p-5 h-full flex flex-col" style={{ minHeight: '120px' }}>
              <p className="text-muted-foreground text-xs mb-2">{content.siteName as string || linkHostname}</p>
              <h3 className="text-foreground font-medium text-sm mb-1 line-clamp-2">
                {linkTitle}
              </h3>
              {content.description && (
                <p className="text-muted-foreground text-xs line-clamp-2 flex-1">
                  {content.description as string}
                </p>
              )}
              <div className="mt-auto pt-3 flex items-center gap-1.5 text-muted-foreground">
                <Link2 className="w-3 h-3" />
                <span className="text-xs uppercase tracking-wide">Link</span>
              </div>
            </div>
          );
        }
      }

      case 'video':
        const duration = content.duration as number || 0;
        const mins = Math.floor(duration / 60);
        const secs = Math.floor(duration % 60);
        const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;
        const videoTitle = content.filename as string || content.summary as string || 'Video';
        
        return (
          <div className="relative bg-white dark:bg-[#2a2a2a] overflow-hidden h-full flex flex-col">
            {/* Thumbnail area */}
            <div className="relative flex-1 min-h-0 bg-black/10 dark:bg-black/30">
              {content.thumbnailUrl ? (
                <img 
                  src={content.thumbnailUrl as string} 
                  alt={content.filename as string || 'Video thumbnail'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                  <Film className="w-8 h-8 text-gray-400" />
                </div>
              )}
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-10 h-10 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center shadow-md">
                  <Play className="w-5 h-5 text-gray-800 dark:text-white ml-0.5" fill="currentColor" />
                </div>
              </div>
              
              {/* Duration badge */}
              {duration > 0 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {durationStr}
                </div>
              )}
            </div>
            
            {/* White bottom section with title, type, and board tag */}
            <div className="p-3 bg-white dark:bg-[#2a2a2a] min-h-[80px] flex flex-col">
              <p className="font-medium text-gray-900 dark:text-white truncate text-sm mb-2">
                {videoTitle}
              </p>
              {/* Type indicator under name */}
              <div className="mt-auto flex items-center gap-1.5 text-gray-400">
                <Film className="w-3 h-3" />
                <span className="text-xs uppercase tracking-wide">Video</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={cn(
          'relative rounded-xl overflow-hidden cursor-pointer',
          'bg-white dark:bg-[#2a2a2a]',
          'transition-all duration-300',
          'hover:shadow-lg hover:-translate-y-1',
          'group',
          compact && 'scale-95'
        )}
        style={{
          // Fixed 3:2 aspect ratio for text-based and video cards
          aspectRatio: useFixedAspect ? '3/2' : undefined
        }}
        onClick={onClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {renderContent()}
        
        {/* Hover Actions - Top Right */}
        <div className={cn(
          'absolute top-3 right-3 flex gap-1 transition-opacity duration-200',
          isHovered ? 'opacity-100' : 'opacity-0'
        )}>
          {onPin && (
            <button 
              className={cn(
                'p-2 backdrop-blur rounded-lg transition-colors',
                isPinned 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-black/50 dark:bg-white/20 text-white hover:bg-black/70 dark:hover:bg-white/30'
              )}
              onClick={(e) => {
                e.stopPropagation();
                onPin();
              }}
              title={isPinned ? 'Pinned' : 'Pin to Wall'}
            >
              <Pin className="w-4 h-4" />
            </button>
          )}
          <button
            className="p-2 backdrop-blur rounded-lg transition-colors bg-black/50 dark:bg-white/20 text-white hover:bg-red-500 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Board indicator - Bottom Right - Shows on hover */}
        {!compact && (
          <div className={cn(
            'transition-opacity duration-200',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}>
            <BoardTag />
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onShare={handleShare}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}

// ── Helper: extract YouTube video ID ───────────────────────────────────

function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) return u.searchParams.get('v');
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0];
    if (u.pathname.includes('/embed/')) return u.pathname.split('/embed/')[1]?.split('?')[0] || null;
  } catch { /* invalid url */ }
  return null;
}

// ── Wall PDF Preview sub-component ─────────────────────────────────────

function WallPdfPreview({ url, filename, size }: { url: string; filename: string; size: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const displaySize = size ? formatFileSize(size) : '';

  return (
    <>
      <div
        className="relative h-full flex flex-col bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20 cursor-pointer group"
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
        style={{ minHeight: '200px' }}
      >
        {/* Decorative page lines */}
        <div className="absolute inset-x-5 top-5 bottom-16 bg-white/60 dark:bg-white/5 rounded-md border border-gray-200/50 dark:border-gray-700/30 flex flex-col p-4 gap-2 overflow-hidden">
          <div className="h-2 w-3/4 bg-gray-200/70 dark:bg-gray-600/30 rounded" />
          <div className="h-2 w-full bg-gray-200/50 dark:bg-gray-600/20 rounded" />
          <div className="h-2 w-5/6 bg-gray-200/50 dark:bg-gray-600/20 rounded" />
          <div className="h-2 w-full bg-gray-200/40 dark:bg-gray-600/15 rounded" />
          <div className="h-2 w-2/3 bg-gray-200/40 dark:bg-gray-600/15 rounded" />
        </div>

        {/* PDF badge */}
        <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider z-10">
          PDF
        </div>

        {/* View button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center shadow-md opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all">
            <Eye className="w-5 h-5 text-gray-800 dark:text-white" />
          </div>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-0 inset-x-0 p-3 bg-white dark:bg-[#2a2a2a]">
          <p className="font-medium text-gray-900 dark:text-white truncate text-sm">{filename}</p>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1.5 text-gray-400">
              <FileText className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">PDF</span>
            </div>
            {displaySize && <span className="text-xs text-gray-400">{displaySize}</span>}
          </div>
        </div>
      </div>

      {/* PDF viewer modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-5xl h-[85vh] p-0 bg-background border overflow-hidden flex flex-col [&>button:last-child]:hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Viewing: {filename}</DialogTitle>
          {/* Header — close + download in same row to avoid overlap */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{filename}</span>
              {displaySize && <span className="text-xs text-muted-foreground flex-shrink-0">({displaySize})</span>}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); url && window.open(url, '_blank'); }}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
              <DialogPrimitive.Close className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>
          </div>
          {url && (
            <div className="flex-1 w-full min-h-0 relative">
              <iframe
                src={url}
                className="w-full h-full border-0"
                title={filename}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Wall YouTube Preview sub-component ─────────────────────────────────

function WallYouTubePreview({ videoId, title, thumbnail, url }: { videoId: string; title: string; thumbnail: string; url: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <>
      <div
        className="relative h-full flex flex-col bg-black/10 dark:bg-black/30 cursor-pointer group"
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
      >
        {/* Thumbnail */}
        <div className="relative flex-1 min-h-0">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 text-gray-800 dark:text-white ml-0.5" fill="currentColor" />
            </div>
          </div>

          {/* YouTube badge */}
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded">
            <svg className="w-3 h-2.5" viewBox="0 0 28 20" fill="currentColor">
              <path d="M27.4 3.1s-.3-1.9-1.1-2.8C25.1-.9 23.7-.9 23-.9 19.2-1.2 14-1.2 14-1.2h0s-5.2 0-9 .3c-.7.1-2.1.1-3.3 1.2C.9 1.2.6 3.1.6 3.1S.3 5.3.3 7.6v2.1c0 2.2.3 4.5.3 4.5s.3 1.9 1.1 2.8c1.2 1.2 2.6 1.1 3.3 1.3C7.4 18.5 14 18.6 14 18.6s5.2 0 9-.3c.7-.1 2.1-.1 3.3-1.2.8-.9 1.1-2.8 1.1-2.8s.3-2.2.3-4.5V7.6c0-2.2-.3-4.5-.3-4.5zM11.1 13V5.5l6.2 3.8-6.2 3.7z" />
            </svg>
            YouTube
          </div>
        </div>

        {/* Title section */}
        <div className="p-3 bg-white dark:bg-[#2a2a2a] min-h-[64px] flex flex-col">
          <p className="font-medium text-gray-900 dark:text-white truncate text-sm mb-1">{title}</p>
          <div className="mt-auto flex items-center gap-1.5 text-gray-400">
            <Film className="w-3 h-3" />
            <span className="text-xs uppercase tracking-wide">Video</span>
          </div>
        </div>
      </div>

      {/* YouTube player modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-4xl p-0 bg-black border-none overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Playing: {title}</DialogTitle>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full border-0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              title={title}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
